import { Collapse, ConfigProvider, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import {
  ConflictRemovalFooter,
  removalLockProps,
  serverSupportsRemoval,
  useRemovalAction,
} from "components/modals/ConflictRemovalFooter";
import { getLeaveConflictPreview } from "hooks/mutations/staff";
import { useCallback, useMemo, useState } from "react";
import { colors } from "shared/theme/color";
import { translate, translateWithVars } from "shared/languages";
import { AffectedOccurrence, LeaveConflictPreview, LeavePreviewForm } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { groupByDate } from "shared/utils";
import { localDateOnly, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Open classes ALWAYS collapse to a plain count, for consistency regardless
// of how few are booked. Private/duet/triplet classes show names instead
// (more useful to an admin deciding who to follow up with), but only while
// the list is short. A private-type class type COULD still be configured
// with a large max_participants, so this caps back to a count above the
// threshold rather than assuming "private" always means a handful.
const MAX_NAMES_INLINE = 3;

// Same cap as the server's MAX_LEAVE_REMOVAL_OCCURRENCES (staff.ts). Above it
// the delete button is not offered at all, so the endpoint's 400 is never
// reached from the UI and nothing partial can happen.
const MAX_REMOVABLE_OCCURRENCES = 300;

const occurrenceSubtitle = (occurrence: AffectedOccurrence) => {
  if (occurrence.booked_customer_count === 0) {
    return translate("leave.conflict.noClientsYet");
  }
  if (
    occurrence.class_type === ClassCategory.Open ||
    occurrence.booked_customer_count > MAX_NAMES_INLINE
  ) {
    return translateWithVars("leave.conflict.participantsCount", {
      count: occurrence.booked_customer_count,
    });
  }
  return occurrence.booked_customer_names.join(", ");
};

const OccurrenceCard = ({ occurrence }: { occurrence: AffectedOccurrence }) => {
  const start = localTzDate(occurrence.occurrence_timestamp);
  const end = start.add(occurrence.duration, "minute");
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colors.lineStrong}`,
        borderRadius: 12,
        background: colors.card,
      }}
    >
      <Text style={{ fontWeight: 500 }}>{occurrence.class_name}</Text>
      <div>
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {start.format("HH:mm")} - {end.format("HH:mm")}
        </Text>
        <Text
          type={occurrence.booked_customer_count > 0 ? undefined : "secondary"}
          style={{ fontSize: textPx(12.5), fontWeight: occurrence.booked_customer_count > 0 ? 500 : 400 }}
        >
          {" "}
          · {occurrenceSubtitle(occurrence)}
        </Text>
        {/* A class that already started (or is running) is listed for
            completeness but never deleted by the button below; the suffix is
            plain muted text, not a badge. */}
        {occurrence.has_started && (
          <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
            {" "}
            · {translate("conflict.startedSuffix")}
          </Text>
        )}
      </div>
    </div>
  );
};

// Day header (date + count), used as the Collapse panel label.
const DayGroupHeader = ({ date, count }: { date: string; count: number }) => (
  <Text style={{ fontWeight: 600 }}>
    {localDateOnly(date).format("dddd, DD MMMM YYYY")}
    <Text style={{ color: colors.ink3, fontWeight: 500, marginLeft: 6 }}>
      · {count} {translate("leave.conflict.classCount")}
    </Text>
  </Text>
);

// One day's occurrence cards — the Collapse panel's body.
const DayGroupBody = ({ occurrences }: { occurrences: AffectedOccurrence[] }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {occurrences.map((occurrence) => (
      <OccurrenceCard
        key={`${occurrence.schedule_id}-${occurrence.occurrence_timestamp}`}
        occurrence={occurrence}
      />
    ))}
  </div>
);

const LeaveConflictWarningContent = ({ preview }: { preview: LeaveConflictPreview }) => {
  const dayGroups = useMemo(
    () => groupByDate(preview.affected_occurrences),
    [preview],
  );
  return (
    <div>
      <Text style={modalSubtitleStyle}>{translate("leave.conflict.description")}</Text>
      {/* All collapsed by default — every header still shows its own date +
          count, so the admin sees the full scope of the leave's impact at a
          glance before drilling into any one day. */}
      <Collapse
        ghost
        style={{ marginTop: 16 }}
        items={dayGroups.map(([date, occurrences]) => ({
          key: date,
          label: <DayGroupHeader date={date} count={occurrences.length} />,
          children: <DayGroupBody occurrences={occurrences} />,
        }))}
      />
    </div>
  );
};

const hasConflicts = (preview: LeaveConflictPreview) =>
  preview.affected_occurrences.length > 0;

// Whether "Hapus Jadwal & Ajukan Cuti" can be offered for this preview: only
// classes that have not started are deleted, and only up to the server cap.
const canOfferRemoval = (preview: LeaveConflictPreview | null) => {
  const all = preview?.affected_occurrences ?? [];
  const deletable = all.filter((occurrence) => !occurrence.has_started).length;
  return serverSupportsRemoval(all) && deletable > 0 && deletable <= MAX_REMOVABLE_OCCURRENCES;
};

const LeaveConflictWarningModal = ({
  open,
  preview,
  confirmLabel,
  removeLabel,
  onConfirm,
  onConfirmWithRemoval,
  onCancel,
}: {
  open: boolean;
  preview: LeaveConflictPreview | null;
  confirmLabel: string;
  removeLabel: string;
  onConfirm: () => void;
  /** Deletes the affected classes and saves the leave in one server call.
   *  Resolves on success (the modal then closes); rejects on failure (the
   *  modal stays open, the mutation's own error toast explains why). */
  onConfirmWithRemoval?: () => Promise<void>;
  onCancel: () => void;
}) => {
  const { removing, runRemoval } = useRemovalAction(onConfirmWithRemoval);

  return (
    // Button colours, width and the in-flight lock come from
    // ConflictRemovalFooter (shared with the working-hours and class-assignment
    // pop-ups). confirmLabel / removeLabel vary by caller (applying a new leave
    // vs restoring a cancelled one reuse this same modal, worded differently).
    <ConfigProvider theme={modalButtonsTheme}>
      <Modal
        open={open}
        title={<ModalHeader tone="amber" title={translate("leave.conflict.title")} />}
        {...removalLockProps(removing, onCancel)}
        destroyOnClose
        // Same reasoning as EmployeeManagementReactivationWarningModal: expanding
        // several days (or one day with many classes) can still grow past the
        // viewport even with the collapse-by-default above. Cap the body in
        // its own scroll region so the title/footer stay reachable.
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
        footer={
          <ConflictRemovalFooter
            removing={removing}
            canRemove={!!onConfirmWithRemoval && canOfferRemoval(preview)}
            removeLabel={removeLabel}
            confirmLabel={confirmLabel}
            onRemove={runRemoval}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        }
      >
        {preview && <LeaveConflictWarningContent preview={preview} />}
      </Modal>
    </ConfigProvider>
  );
};

/**
 * Gates a leave action (applying a new one, or restoring a cancelled one)
 * behind a preview of who it would affect. Mirrors useClashWarning
 * (ClashWarningModal.tsx):
 *  - checkAndConfirm(form, onConfirm, onConfirmWithRemoval?): runs the
 *    preview; with affected occurrences it opens the warning modal and only
 *    runs onConfirm (proceed anyway) or onConfirmWithRemoval (delete the
 *    affected classes and proceed, in one server call) on the matching
 *    button; with none it runs onConfirm immediately. A failed check
 *    proceeds (same fail-open reasoning: this is informational, not a
 *    hard block, so a broken preview must never prevent the action).
 *  - leavePreviewWarning: the modal element to render in the caller's JSX.
 * confirmLabel / removeLabel let each caller word the two proceed buttons
 * for its own action ("Tetap Ajukan Cuti" / "Hapus Jadwal & Ajukan Cuti" vs
 * "Tetap Pulihkan" / "Hapus Jadwal & Pulihkan Cuti") while sharing
 * everything else about the modal.
 */
export const useLeavePreview = (
  confirmLabel: string = translate("leave.conflict.confirm"),
  removeLabel: string = translate("leave.conflict.removeAndApply"),
) => {
  const [pending, setPending] = useState<{
    preview: LeaveConflictPreview;
    onConfirm: () => void;
    onConfirmWithRemoval?: () => Promise<void>;
  } | null>(null);

  const checkAndConfirm = useCallback(
    async (
      form: LeavePreviewForm,
      onConfirm: () => void,
      onConfirmWithRemoval?: () => Promise<void>,
    ) => {
      let preview: LeaveConflictPreview;
      try {
        preview = await getLeaveConflictPreview(form);
      } catch (error) {
        console.error("Leave conflict preview failed; proceeding without warning.", error);
        onConfirm();
        return;
      }
      if (!hasConflicts(preview)) {
        onConfirm();
        return;
      }
      setPending({ preview, onConfirm, onConfirmWithRemoval });
    },
    [],
  );

  const leavePreviewWarning = (
    <LeaveConflictWarningModal
      open={!!pending}
      preview={pending?.preview ?? null}
      confirmLabel={confirmLabel}
      removeLabel={removeLabel}
      onConfirm={() => {
        pending?.onConfirm();
        setPending(null);
      }}
      onConfirmWithRemoval={
        pending?.onConfirmWithRemoval
          ? async () => {
              await pending.onConfirmWithRemoval?.();
              setPending(null);
            }
          : undefined
      }
      onCancel={() => setPending(null)}
    />
  );

  return { checkAndConfirm, leavePreviewWarning };
};
